import { WalletAgeCheckResult } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { useHubUser } from "@/hooks/user/useHubUser";
import { useQuery } from "@tanstack/react-query";

const STALE_TIME_MS = 60 * 60 * 1000; // 1 hour

interface UseWalletAgeEligibilityProps {
  walletAddress?: string;
  enabled?: boolean;
}

interface UseWalletAgeEligibilityResult {
  isEligible: boolean | null;
  isLoading: boolean;
  error: Error | null;
  data: WalletAgeCheckResult | undefined;
}

/**
 * Hook to check wallet age eligibility for DEX campaigns.
 * Wallets must have at least one transaction older than 2 weeks to participate.
 *
 * Results are cached for 1 week to match backend caching.
 */
export const useWalletAgeEligibility = ({
  walletAddress,
  enabled = true,
}: UseWalletAgeEligibilityProps): UseWalletAgeEligibilityResult => {
  const api = useApi();
  const { auth } = useHubUser();

  const query = useQuery({
    queryKey: [QueryKeys.WALLET_AGE_ELIGIBILITY, walletAddress],
    queryFn: async (): Promise<WalletAgeCheckResult> => {
      if (!walletAddress) throw new Error("Wallet address is required");

      const response = await api.getWithAuth(auth, API_ENDPOINT.ONCHAIN_SWAP_WALLET_AGE_ELIGIBILITY, {
        queryParams: { walletAddress },
      });

      return response;
    },
    enabled: enabled && !!walletAddress && !!auth,
    staleTime: STALE_TIME_MS,
    gcTime: STALE_TIME_MS,
  });

  return {
    isEligible: query.data?.isEligible ?? null,
    isLoading: query.isLoading,
    error: query.error,
    data: query.data,
  };
};
